import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Divider,
  Paper,
  Tabs,
  Tab,
  Alert,
  MenuItem,
  FormControlLabel,
  Checkbox
} from '@mui/material';
import {
  Calculate,
  CompareArrows,
  Search,
  Close,
  ArrowForward,
  CheckCircle,
  HelpOutline,
  ReceiptLong,
  AccountBalance,
  Assessment,
  MonetizationOn,
  Security,
  Work,
  FlightTakeoff,
  Description,
  Savings,
  AutoAwesome,
  TrendingUp,
  Domain,
  Construction,
  PostAdd
} from '@mui/icons-material';
import { TaxYearSelector } from '../components/TaxYearSelector';
import {
  TaxFramework,
  SECTION_MAPPINGS,
  convertTaxYear,
  calculateIncomeTax,
  TaxCalculationInput
} from '../utils/taxRulesEngine';
import SEOHead from '../components/SEOHead';

interface ToolItem {
  id: string;
  title: string;
  category: 'calculate' | 'investments' | 'business' | 'transition' | 'eligibility' | 'deductions' | 'special';
  badge?: string;
  description: string;
  icon: React.ReactNode;
}

const CATEGORIES = [
  { id: 'all', label: 'All Utilities', icon: <AutoAwesome sx={{ fontSize: '1.1rem' }} /> },
  { id: 'calculate', label: 'Tax Calculators', icon: <Calculate sx={{ fontSize: '1.1rem' }} /> },
  { id: 'investments', label: 'Wealth & Investments', icon: <TrendingUp sx={{ fontSize: '1.1rem' }} /> },
  { id: 'business', label: 'Business & GST', icon: <Domain sx={{ fontSize: '1.1rem' }} /> },
  { id: 'transition', label: '1961 Act → 2025 Act', icon: <CompareArrows sx={{ fontSize: '1.1rem' }} /> },
  { id: 'eligibility', label: 'Eligibility & Compliance', icon: <CheckCircle sx={{ fontSize: '1.1rem' }} /> },
  { id: 'deductions', label: 'Deductions & Savings', icon: <Savings sx={{ fontSize: '1.1rem' }} /> },
  { id: 'special', label: 'Special & NRI', icon: <FlightTakeoff sx={{ fontSize: '1.1rem' }} /> }
];

const TOOLS: ToolItem[] = [
  // 🧮 Calculate Tax
  {
    id: 'tax-calculator',
    title: 'Income Tax Calculator 2026–27',
    category: 'calculate',
    badge: 'Popular',
    description: 'Calculate income tax under AY 2026–27 (1961 Act) or Tax Year 2026–27 (2025 Act) with instant slab-by-slab comparison.',
    icon: <Calculate sx={{ color: '#123D6B' }} />
  },
  {
    id: 'old-vs-new',
    title: 'Old Act vs New Act Tax Calculator',
    category: 'calculate',
    badge: 'Transition',
    description: 'Side-by-side tax comparison between 1961 Act framework and 2025 Act framework.',
    icon: <CompareArrows sx={{ color: '#C59B27' }} />
  },
  {
    id: 'salary-tax',
    title: 'Salary Tax Calculator',
    category: 'calculate',
    description: 'Break down basic salary, HRA, allowances, and perquisites to calculate net taxable salary.',
    icon: <MonetizationOn sx={{ color: '#10B981' }} />
  },
  {
    id: 'capital-gains',
    title: 'Capital Gains Tax Calculator',
    category: 'calculate',
    description: 'Compute short-term and long-term capital gains tax for stocks, mutual funds, and property.',
    icon: <Assessment sx={{ color: '#8B5CF6' }} />
  },
  {
    id: 'business-tax',
    title: 'Business & Freelancer Tax Calculator',
    category: 'calculate',
    badge: '44AD / 44ADA',
    description: 'Presumptive tax calculator for small businesses, consultants, and digital freelancers.',
    icon: <Work sx={{ color: '#F59E0B' }} />
  },
  {
    id: 'crypto-tax',
    title: 'Crypto / VDA Tax Calculator',
    category: 'calculate',
    description: 'Calculate 30% VDA tax and 1% TDS on Bitcoin, Crypto, and Virtual Assets.',
    icon: <Security sx={{ color: '#EF4444' }} />
  },
  {
    id: 'gratuity-calculator',
    title: 'Gratuity Calculator',
    category: 'calculate',
    description: 'Calculate gratuity payout for private and government employees upon retirement or resignation.',
    icon: <Work sx={{ color: '#10B981' }} />
  },

  // 📈 Wealth & Investments (NEW SEO)
  {
    id: 'sip-calculator',
    title: 'SIP & Mutual Fund Calculator',
    category: 'investments',
    badge: 'Trending',
    description: 'Calculate future value, wealth gain, and expected returns of your monthly SIP investments.',
    icon: <TrendingUp sx={{ color: '#3B82F6' }} />
  },
  {
    id: 'ppf-calculator',
    title: 'PPF Maturity Calculator',
    category: 'investments',
    description: 'Calculate maturity amount and tax-free interest earned on your Public Provident Fund.',
    icon: <Savings sx={{ color: '#F59E0B' }} />
  },
  {
    id: 'nps-calculator',
    title: 'NPS Pension & Tax Calculator',
    category: 'investments',
    description: 'Calculate National Pension System corpus, monthly pension, and extra 80CCD(1B) tax savings.',
    icon: <AccountBalance sx={{ color: '#8B5CF6' }} />
  },

  // 🏢 Business & GST (NEW SEO)
  {
    id: 'gst-calculator',
    title: 'GST Calculator (Inclusive/Exclusive)',
    category: 'business',
    badge: 'Essential',
    description: 'Instantly add or remove GST from prices to generate accurate B2B and B2C invoices.',
    icon: <ReceiptLong sx={{ color: '#123D6B' }} />
  },
  {
    id: 'startup-valuation',
    title: 'Startup Valuation Calculator',
    category: 'business',
    description: 'Estimate your startup’s pre-money and post-money valuation for fundraising and equity rounds.',
    icon: <Domain sx={{ color: '#C59B27' }} />
  },
  {
    id: 'hsn-finder',
    title: 'GST HSN/SAC Code Finder',
    category: 'business',
    description: 'Search and find the exact HSN or SAC code and applicable GST rate for any product or service.',
    icon: <Search sx={{ color: '#64748B' }} />
  },
  {
    id: 'invoice-generator',
    title: 'Free GST Invoice Generator',
    category: 'business',
    badge: 'Generator',
    description: 'Create, customize, and download professional PDF GST invoices for your clients instantly.',
    icon: <PostAdd sx={{ color: '#10B981' }} />
  },

  // 🔄 Transition Utilities
  {
    id: 'section-converter',
    title: 'Old Section ➔ New Section Converter',
    category: 'transition',
    badge: '2025 Act',
    description: 'Search old sections like 80C, 24(b), 80D, 115BAC to find corresponding 2025 Act provisions.',
    icon: <CompareArrows sx={{ color: '#123D6B' }} />
  },
  {
    id: 'ay-converter',
    title: 'Tax Year / AY Converter',
    category: 'transition',
    description: 'Convert Assessment Year (AY) to Tax Year and identify governing statutory acts.',
    icon: <ReceiptLong sx={{ color: '#3B82F6' }} />
  },
  {
    id: 'tax-navigator',
    title: 'Fingard Tax Navigator',
    category: 'transition',
    badge: 'Search',
    description: 'Universal tax search tool for any section, form, or plain-language tax query.',
    icon: <Search sx={{ color: '#C59B27' }} />
  },
  {
    id: 'form-converter',
    title: 'Form Converter',
    category: 'transition',
    description: 'Map old tax forms to new statutory forms under the Income-tax Act, 2025.',
    icon: <Description sx={{ color: '#64748B' }} />
  },

  // 📋 Check Eligibility & Compliance
  {
    id: 'itr-eligibility',
    title: 'ITR Eligibility Checker',
    category: 'eligibility',
    badge: 'Diagnostic',
    description: 'Answer 4 simple questions to determine your ideal ITR Form (ITR-1, 2, 3, 4, U).',
    icon: <CheckCircle sx={{ color: '#10B981' }} />
  },
  {
    id: 'tds-calculator',
    title: 'TDS Calculator',
    category: 'eligibility',
    description: 'Calculate TDS deduction rates, net payout, and compliance requirements for contractors & professionals.',
    icon: <AccountBalance sx={{ color: '#3B82F6' }} />
  },
  {
    id: 'advance-tax',
    title: 'Advance Tax Calculator',
    category: 'eligibility',
    description: 'Calculate quarterly advance tax installment obligations and key due dates.',
    icon: <ReceiptLong sx={{ color: '#F59E0B' }} />
  },
  {
    id: 'late-interest',
    title: 'Late Filing & Interest Calculator',
    category: 'eligibility',
    badge: 'Sec 234',
    description: 'Calculate late filing fees under Section 234F and interest penalties for delayed returns.',
    icon: <HelpOutline sx={{ color: '#EF4444' }} />
  },

  // 💰 Deductions & Savings
  {
    id: '80c-calculator',
    title: '80C / Sec 123 Tax Saving Calculator',
    category: 'deductions',
    description: 'Optimize investments in PPF, ELSS, EPF, LIC, and tuition fees to maximize savings.',
    icon: <Savings sx={{ color: '#10B981' }} />
  },
  {
    id: 'hra-calculator',
    title: 'HRA Calculator & Rent Receipt Generator',
    category: 'deductions',
    description: 'Calculate House Rent Allowance tax exemption and generate downloadable rent receipts.',
    icon: <ReceiptLong sx={{ color: '#8B5CF6' }} />
  },
  {
    id: 'homeloan-calculator',
    title: 'Home Loan Tax Benefit Calculator',
    category: 'deductions',
    description: 'Compute tax savings on Principal (80C) and Interest (Sec 24b / Sec 42) for home loans.',
    icon: <AccountBalance sx={{ color: '#123D6B' }} />
  },

  // 💼 Special Cases & NRI
  {
    id: 'nri-calculator',
    title: 'NRI Tax Calculator & DTAA Advisor',
    category: 'special',
    badge: 'NRI Special',
    description: 'Determine residential status, Indian taxable income, and Double Taxation Relief (DTAA).',
    icon: <FlightTakeoff sx={{ color: '#3B82F6' }} />
  }
];

export const TaxTools: React.FC = () => {
  const [selectedFramework, setSelectedFramework] = useState<TaxFramework>('TAX_YEAR_2026_27_2025_ACT');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [activeToolId, setActiveToolId] = useState<string | null>(null);

  // Income Tax Engine Input State
  const [taxInput, setTaxInput] = useState<TaxCalculationInput>({
    taxpayerType: 'individual_below_60',
    framework: 'TAX_YEAR_2026_27_2025_ACT',
    salaryIncome: 1200000,
    businessIncome: 0,
    housePropertyIncome: 0,
    capitalGains: 0,
    otherIncome: 0,
    deductions80C: 150000,
    deductions80D: 25000,
    otherDeductions: 0
  });

  const [sectionSearch, setSectionSearch] = useState<string>('');
  const [ayInput, setAyInput] = useState<string>('2026-27');

  // SIP Calculator State & Logic
  const [sipState, setSipState] = useState({ monthlyInvestment: 5000, rate: 12, years: 10 });
  const sipResult = useMemo(() => {
    const P = sipState.monthlyInvestment;
    const i = (sipState.rate / 100) / 12;
    const n = sipState.years * 12;
    const invested = P * n;
    const maturity = P * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
    return { invested, returns: maturity - invested, maturity };
  }, [sipState]);

  // PPF Calculator State & Logic
  const [ppfState, setPpfState] = useState({ yearlyInvestment: 150000, years: 15 });
  const ppfResult = useMemo(() => {
    const P = ppfState.yearlyInvestment;
    const i = 7.1 / 100;
    const n = ppfState.years;
    const invested = P * n;
    const maturity = P * ((Math.pow(1 + i, n) - 1) / i);
    return { invested, returns: maturity - invested, maturity };
  }, [ppfState]);

  // GST Calculator State & Logic
  const [gstState, setGstState] = useState<{amount: number, rate: number, type: 'inclusive' | 'exclusive'}>({ amount: 10000, rate: 18, type: 'exclusive' });
  const gstResult = useMemo(() => {
    let original = 0, gstAmount = 0, net = 0;
    if (gstState.type === 'exclusive') {
      original = gstState.amount;
      gstAmount = original * (gstState.rate / 100);
      net = original + gstAmount;
    } else {
      net = gstState.amount;
      original = net / (1 + (gstState.rate / 100));
      gstAmount = net - original;
    }
    return { original, gstAmount, net };
  }, [gstState]);

  // Gratuity Calculator State & Logic
  const [gratuityState, setGratuityState] = useState({ salary: 50000, years: 5 });
  const gratuityResult = useMemo(() => {
    // Basic Gratuity Formula: (15 * Last Drawn Salary * Tenure) / 26
    const amount = (15 * gratuityState.salary * gratuityState.years) / 26;
    return { amount };
  }, [gratuityState]);

  // NPS Calculator State & Logic
  const [npsState, setNpsState] = useState({ monthly: 5000, age: 30, rate: 10, annuityRate: 6 });
  const npsResult = useMemo(() => {
    const P = npsState.monthly;
    const i = (npsState.rate / 100) / 12;
    const n = (60 - npsState.age) * 12; // Retirement at 60
    const invested = P * n;
    const corpus = P * ((Math.pow(1 + i, n) - 1) / i) * (1 + i);
    const taxSaved = Math.min(invested, 50000) * 0.312; // Max 50k under 80CCD(1B) assuming 30% slab
    const lumpSum = corpus * 0.6; // 60% tax-free lump sum
    const annuityCorpus = corpus * 0.4;
    const monthlyPension = (annuityCorpus * (npsState.annuityRate / 100)) / 12;
    return { invested, corpus, taxSaved, lumpSum, monthlyPension };
  }, [npsState]);

  // Startup Valuation (Revenue Multiple)
  const [valState, setValState] = useState({ revenue: 5000000, multiple: 5, cash: 1000000, debt: 500000 });
  const valResult = useMemo(() => {
    const ev = valState.revenue * valState.multiple; // Enterprise Value
    const equityVal = ev + valState.cash - valState.debt; // Equity Value
    return { ev, equityVal };
  }, [valState]);

  // Capital Gains Calculator
  const [cgState, setCgState] = useState({ asset: 'equity' as 'equity' | 'property' | 'debt', buyPrice: 500000, sellPrice: 800000, months: 14 });
  const cgResult = useMemo(() => {
    const gain = Math.max(0, cgState.sellPrice - cgState.buyPrice);
    if (gain <= 0) return { gain: 0, taxable: 0, tax: 0, type: 'No Gain', rate: 0 };
    const isLong = cgState.asset === 'equity' ? cgState.months > 12 : cgState.months > 24;
    if (cgState.asset === 'equity') {
      if (isLong) { const taxable = Math.max(0, gain - 125000); return { gain, taxable, tax: Math.round(taxable * 0.125), type: 'LTCG', rate: 12.5 }; }
      return { gain, taxable: gain, tax: Math.round(gain * 0.20), type: 'STCG', rate: 20 };
    }
    if (isLong) return { gain, taxable: gain, tax: Math.round(gain * 0.125), type: 'LTCG', rate: 12.5 };
    return { gain, taxable: gain, tax: Math.round(gain * 0.20), type: 'STCG (Slab)', rate: 20 };
  }, [cgState]);

  // Business / Freelancer Tax (44AD / 44ADA)
  const [bizState, setBizState] = useState({ scheme: '44AD' as '44AD' | '44ADA', turnover: 5000000, digitalPercent: 100, regime: 'new' as 'old' | 'new' });
  const bizResult = useMemo(() => {
    const t = Math.max(0, bizState.turnover);
    const dp = Math.min(100, Math.max(0, bizState.digitalPercent)) / 100;
    const cashReceipts = t * (1 - dp);
    const digitalReceipts = t * dp;
    let presumptiveIncome: number;
    if (bizState.scheme === '44ADA') { presumptiveIncome = t * 0.50; }
    else { presumptiveIncome = cashReceipts * 0.08 + digitalReceipts * 0.06; }
    // Simplified slab for new regime
    let tax = 0;
    const ti = presumptiveIncome;
    if (bizState.regime === 'new') {
      if (ti > 1500000) tax = (ti - 1500000) * 0.30 + 150000;
      else if (ti > 1200000) tax = (ti - 1200000) * 0.20 + 90000;
      else if (ti > 900000) tax = (ti - 900000) * 0.15 + 45000;
      else if (ti > 600000) tax = (ti - 600000) * 0.10 + 15000;
      else if (ti > 300000) tax = (ti - 300000) * 0.05;
      if (ti <= 700000) tax = 0;
    } else {
      if (ti > 1000000) tax = (ti - 1000000) * 0.30 + 112500;
      else if (ti > 500000) tax = (ti - 500000) * 0.20 + 12500;
      else if (ti > 250000) tax = (ti - 250000) * 0.05;
      if (ti <= 500000) tax = 0;
    }
    tax = Math.round(tax * 1.04); // 4% cess
    return { presumptiveIncome, tax, effectiveRate: t > 0 ? ((tax / t) * 100).toFixed(2) : '0.00' };
  }, [bizState]);

  // Crypto / VDA Tax Calculator
  const [cryptoState, setCryptoState] = useState({ buyPrice: 100000, sellPrice: 250000, transferExpenses: 0 });
  const cryptoResult = useMemo(() => {
    const gain = Math.max(0, cryptoState.sellPrice - cryptoState.buyPrice);
    const tax = Math.round(gain * 0.30);
    const cess = Math.round(tax * 0.04);
    const tds = Math.round(cryptoState.sellPrice * 0.01); // 1% TDS on sale
    const netProfit = gain - tax - cess;
    return { gain, tax, cess, totalTax: tax + cess, tds, netProfit };
  }, [cryptoState]);

  // TDS Calculator
  const [tdsState, setTdsState] = useState({ section: '194J' as string, gross: 500000 });
  const tdsResult = useMemo(() => {
    const rates: Record<string, { rate: number; label: string }> = {
      '194C_ind': { rate: 1, label: 'Sec 194C – Individual Contractor' },
      '194C_co': { rate: 2, label: 'Sec 194C – Company Contractor' },
      '194H': { rate: 5, label: 'Sec 194H – Brokerage / Commission' },
      '194I_land': { rate: 10, label: 'Sec 194I – Rent on Land/Building' },
      '194I_equip': { rate: 2, label: 'Sec 194I – Rent on Equipment' },
      '194J': { rate: 10, label: 'Sec 194J – Professional / Technical Fees' },
      '194A': { rate: 10, label: 'Sec 194A – Interest (Non-Bank)' },
      '194B': { rate: 30, label: 'Sec 194B – Lottery / Game Winnings' },
      '194N': { rate: 2, label: 'Sec 194N – Cash Withdrawal > ₹1Cr' },
    };
    const info = rates[tdsState.section] || { rate: 10, label: tdsState.section };
    const deducted = Math.round(Math.max(0, tdsState.gross) * (info.rate / 100));
    return { label: info.label, rate: info.rate, deducted, netPayable: Math.max(0, tdsState.gross) - deducted };
  }, [tdsState]);

  // Advance Tax Calculator
  const [advTaxState, setAdvTaxState] = useState({ totalTax: 200000, tdsPaid: 50000 });
  const advTaxResult = useMemo(() => {
    const net = Math.max(0, advTaxState.totalTax - advTaxState.tdsPaid);
    const q1 = Math.round(net * 0.15); // 15% by Jun 15
    const q2 = Math.round(net * 0.45) - q1; // 45% by Sep 15
    const q3 = Math.round(net * 0.75) - q1 - q2; // 75% by Dec 15
    const q4 = net - q1 - q2 - q3; // 100% by Mar 15
    return { net, q1, q2, q3, q4 };
  }, [advTaxState]);

  // Late Filing & Interest Calculator (Sec 234A, 234B, 234F)
  const [lateState, setLateState] = useState({ taxPayable: 100000, taxPaid: 50000, monthsLate: 3, income: 600000 });
  const lateResult = useMemo(() => {
    const unpaid = Math.max(0, lateState.taxPayable - lateState.taxPaid);
    const months = Math.max(0, Math.min(120, lateState.monthsLate));
    const interest234A = Math.round(unpaid * 0.01 * months); // 1% per month
    const interest234B = Math.round(unpaid * 0.01 * months); // 1% per month on advance tax shortfall
    const fee234F = lateState.income > 500000 ? 5000 : 1000;
    const total = interest234A + interest234B + fee234F;
    return { interest234A, interest234B, fee234F, total, unpaid };
  }, [lateState]);

  // 80C Tax Saving Optimizer
  const [sec80cState, setSec80cState] = useState({ epf: 21600, ppf: 50000, elss: 0, lic: 30000, tuition: 0, others: 0, slab: 30 });
  const sec80cResult = useMemo(() => {
    const total = sec80cState.epf + sec80cState.ppf + sec80cState.elss + sec80cState.lic + sec80cState.tuition + sec80cState.others;
    const eligible = Math.min(total, 150000);
    const taxSaved = Math.round(eligible * (sec80cState.slab / 100) * 1.04); // including cess
    const remaining = Math.max(0, 150000 - total);
    return { total, eligible, taxSaved, remaining };
  }, [sec80cState]);

  // HRA Calculator
  const [hraState, setHraState] = useState({ basic: 50000, da: 0, hraReceived: 20000, rentPaid: 25000, metro: true });
  const hraResult = useMemo(() => {
    const salary = hraState.basic + hraState.da;
    const annualSalary = salary * 12;
    const annualHra = hraState.hraReceived * 12;
    const annualRent = hraState.rentPaid * 12;
    const a1 = annualHra;
    const a2 = Math.max(0, annualRent - annualSalary * 0.10);
    const a3 = annualSalary * (hraState.metro ? 0.50 : 0.40);
    const exempt = Math.max(0, Math.min(a1, a2, a3));
    const taxable = Math.max(0, annualHra - exempt);
    return { a1, a2, a3, exempt, taxable, monthlyExempt: Math.round(exempt / 12) };
  }, [hraState]);

  // Home Loan Tax Benefit Calculator
  const [hlState, setHlState] = useState({ principal: 3000000, interestRate: 8.5, tenure: 20, yearlyPrincipal: 150000, yearlyInterest: 250000, selfOccupied: true });
  const hlResult = useMemo(() => {
    const principalBenefit = Math.min(hlState.yearlyPrincipal, 150000); // 80C cap
    const interestCap = hlState.selfOccupied ? 200000 : hlState.yearlyInterest; // Sec 24(b): 2L cap for self-occupied
    const interestBenefit = Math.min(hlState.yearlyInterest, interestCap);
    const totalBenefit = principalBenefit + interestBenefit;
    const taxSaved30 = Math.round(totalBenefit * 0.312); // 30% slab + 4% cess
    const taxSaved20 = Math.round(totalBenefit * 0.208);
    const emi = (() => {
      const r = (hlState.interestRate / 100) / 12;
      const n = hlState.tenure * 12;
      if (r === 0) return Math.round(hlState.principal / n);
      return Math.round(hlState.principal * r * Math.pow(1 + r, n) / (Math.pow(1 + r, n) - 1));
    })();
    return { principalBenefit, interestBenefit, totalBenefit, taxSaved30, taxSaved20, emi };
  }, [hlState]);

  // ITR Eligibility
  const [itrState, setItrState] = useState({ hasSalary: true, hasBusinessIncome: false, hasCG: false, hasMultipleHP: false, foreignAssets: false, turnoverAbove1Cr: false, isDirector: false, hasUnlisted: false });
  const itrForm = useMemo(() => {
    if (itrState.foreignAssets || itrState.isDirector || itrState.hasUnlisted) return { form: 'ITR-2', reason: 'You have foreign assets, are a director in a company, or hold unlisted shares.' };
    if (itrState.hasBusinessIncome && itrState.turnoverAbove1Cr) return { form: 'ITR-3', reason: 'Business income with turnover above ₹1 Crore requires full audit and ITR-3.' };
    if (itrState.hasBusinessIncome) return { form: 'ITR-4 (Sugam)', reason: 'Business/Professional income under presumptive taxation (44AD/44ADA).' };
    if (itrState.hasCG || itrState.hasMultipleHP) return { form: 'ITR-2', reason: 'Capital gains or multiple house property requires ITR-2.' };
    if (itrState.hasSalary) return { form: 'ITR-1 (Sahaj)', reason: 'Salaried individual with income up to ₹50L from salary, one HP, and other sources.' };
    return { form: 'ITR-1 (Sahaj)', reason: 'Standard return for individuals with simple income sources.' };
  }, [itrState]);

  // NRI Calculator
  const [nriState, setNriState] = useState({ daysInIndia: 120, indianIncome: 1500000, foreignIncome: 3000000, dtaaCountry: 'USA' });
  const nriResult = useMemo(() => {
    let status = 'Non-Resident (NR)';
    let taxableIncome = nriState.indianIncome;
    if (nriState.daysInIndia >= 182) { status = 'Resident & Ordinarily Resident (ROR)'; taxableIncome = nriState.indianIncome + nriState.foreignIncome; }
    else if (nriState.daysInIndia >= 120 && nriState.indianIncome > 1500000) { status = 'Resident but Not Ordinarily Resident (RNOR)'; taxableIncome = nriState.indianIncome; }
    // Simplified tax on Indian income
    let tax = 0;
    if (taxableIncome > 1500000) tax = (taxableIncome - 1500000) * 0.30 + 150000;
    else if (taxableIncome > 1200000) tax = (taxableIncome - 1200000) * 0.20 + 90000;
    else if (taxableIncome > 900000) tax = (taxableIncome - 900000) * 0.15 + 45000;
    else if (taxableIncome > 600000) tax = (taxableIncome - 600000) * 0.10 + 15000;
    else if (taxableIncome > 300000) tax = (taxableIncome - 300000) * 0.05;
    if (taxableIncome <= 700000) tax = 0;
    tax = Math.round(tax * 1.04);
    return { status, taxableIncome, tax, dtaaRelief: status === 'NR' ? 'Full DTAA relief available' : 'Partial DTAA credit may apply' };
  }, [nriState]);

  const filteredTools = useMemo(() => {
    return TOOLS.filter(tool => {
      const matchesCat = activeCategory === 'all' || tool.category === activeCategory;
      const matchesSearch = tool.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                            tool.description.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesCat && matchesSearch;
    });
  }, [activeCategory, searchQuery]);

  const filteredSections = useMemo(() => {
    if (!sectionSearch.trim()) return SECTION_MAPPINGS;
    const q = sectionSearch.toLowerCase();
    return SECTION_MAPPINGS.filter(s =>
      s.oldSection.toLowerCase().includes(q) ||
      s.newSection.toLowerCase().includes(q) ||
      s.topic.toLowerCase().includes(q)
    );
  }, [sectionSearch]);

  const taxResult = useMemo(() => calculateIncomeTax(taxInput), [taxInput]);
  const ayResult = useMemo(() => convertTaxYear(ayInput), [ayInput]);

  return (
    <Box sx={{ bgcolor: '#F8FAFC', minHeight: '100vh', pb: 12 }}>
      <SEOHead
        title="Free Tax Calculators — Income Tax, HRA, Capital Gains, SIP | Fingard Partners"
        description="Use Fingard Partners' free online tax calculators for Income Tax (Old vs New Regime), HRA Exemption, Capital Gains, SIP Returns, Gratuity, and more."
        keywords="Tax Calculator India, Income Tax Calculator, HRA Calculator, Capital Gains Calculator, Old vs New Tax Regime, Free Tax Tools"
        canonicalUrl="/tax-tools"
      />
      {/* Sleek Minimalist Hero Header */}
      <Box
        sx={{
          background: 'linear-gradient(130deg, #0B2847 0%, #123D6B 60%, #2A5A8D 100%)',
          color: '#FFFFFF',
          pt: { xs: 7, md: 10 },
          pb: { xs: 10, md: 12 },
          textAlign: 'center',
          position: 'relative'
        }}
      >
        <Container maxWidth="md">
          <Chip
            icon={<AutoAwesome sx={{ color: '#C59B27 !important', fontSize: '1rem !important' }} />}
            label="Income-tax Act 2025 · AY 2026–27 · 1961 Act"
            sx={{ bgcolor: 'rgba(197, 155, 39, 0.15)', color: '#FFD700', fontWeight: 700, mb: 3, border: '1px solid rgba(197, 155, 39, 0.3)' }}
          />

          <Typography
            variant="h3"
            fontWeight={800}
            sx={{ fontSize: { xs: '2.2rem', md: '3.2rem' }, letterSpacing: '-0.02em', lineHeight: 1.15, mb: 2 }}
          >
            Fingard Tax Tools
          </Typography>

          <Typography variant="h6" sx={{ color: '#E2E8F0', fontWeight: 400, fontSize: { xs: '1rem', md: '1.15rem' }, mb: 5, maxWidth: 640, mx: 'auto', opacity: 0.9 }}>
            Calculate taxes, convert sections, and compare AY 2026–27 under the 1961 Act against the new Income-tax Act, 2025.
          </Typography>

          {/* Clean Floating Search Bar */}
          <Paper
            elevation={24}
            sx={{
              p: '6px 6px 6px 20px',
              borderRadius: '50px',
              bgcolor: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              maxWidth: 620,
              mx: 'auto',
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0px 20px 40px rgba(11, 40, 71, 0.25)'
            }}
          >
            <Search sx={{ color: '#64748B', mr: 1.5, fontSize: '1.3rem' }} />
            <TextField
              fullWidth
              variant="standard"
              placeholder="Search any tool or section (e.g. 80C, HRA, Capital Gains)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{ disableUnderline: true }}
              sx={{ '& input': { fontSize: '1.05rem', color: '#0F172A', '&::placeholder': { color: '#94A3B8', opacity: 1 } } }}
            />
            <Button
              variant="contained"
              disableElevation
              onClick={() => setActiveToolId('section-converter')}
              sx={{
                borderRadius: '40px',
                px: 3.5,
                py: 1.2,
                ml: 1,
                bgcolor: '#123D6B',
                color: '#FFFFFF',
                fontWeight: 700,
                fontSize: '0.95rem',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
                '&:hover': { bgcolor: '#0B2847', transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(11,40,71,0.2)' }
              }}
            >
              Section Converter
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Main Body */}
      <Container maxWidth="lg" sx={{ mt: -4 }}>
        {/* Global Framework Selector Bar */}
        <TaxYearSelector
          selectedFramework={selectedFramework}
          onChange={(fw) => {
            setSelectedFramework(fw);
            setTaxInput(prev => ({ ...prev, framework: fw }));
          }}
          compact
        />

        {/* Clean Segmented Category Tabs */}
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', mb: 4, p: 0.8 }}>
          <Tabs
            value={activeCategory}
            onChange={(_, val) => setActiveCategory(val)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '0.9rem',
                minHeight: 44,
                borderRadius: 2.5,
                mx: 0.5,
                color: '#64748B',
                '&.Mui-selected': {
                  bgcolor: '#123D6B',
                  color: '#FFFFFF'
                }
              },
              '& .MuiTabs-indicator': { display: 'none' }
            }}
          >
            {CATEGORIES.map(cat => (
              <Tab
                key={cat.id}
                value={cat.id}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {cat.icon}
                    <span>{cat.label}</span>
                  </Box>
                }
              />
            ))}
          </Tabs>
        </Paper>

        {/* Tools Cards Grid */}
        <Grid container spacing={2.5}>
          {filteredTools.map(tool => (
            <Grid item xs={12} sm={6} md={4} key={tool.id}>
              <Card
                elevation={0}
                onClick={() => setActiveToolId(tool.id)}
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3.5,
                  border: '1px solid #E2E8F0',
                  bgcolor: '#FFFFFF',
                  cursor: 'pointer',
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    transform: 'translateY(-3px)',
                    boxShadow: '0px 12px 24px rgba(15, 23, 42, 0.06)',
                    borderColor: '#C59B27'
                  }
                }}
              >
                <CardContent sx={{ p: 2.8, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                    <Box sx={{ p: 1, borderRadius: 2, bgcolor: 'rgba(18, 61, 107, 0.05)', display: 'flex' }}>
                      {tool.icon}
                    </Box>
                    {tool.badge && (
                      <Chip
                        label={tool.badge}
                        size="small"
                        sx={{ bgcolor: '#FFFBEB', color: '#B45309', fontWeight: 800, fontSize: '0.7rem', height: 22 }}
                      />
                    )}
                  </Box>

                  <Typography variant="h6" fontWeight={700} color="#0F172A" sx={{ fontSize: '1.02rem', mb: 0.8 }}>
                    {tool.title}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1, fontSize: '0.86rem', lineHeight: 1.5 }}>
                    {tool.description}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', color: '#123D6B', fontWeight: 700, fontSize: '0.85rem' }}>
                    <span>Launch Utility</span>
                    <ArrowForward sx={{ fontSize: '0.9rem', ml: 0.5 }} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ========================================================= */}
      {/* TOOL MODAL 1: INCOME TAX CALCULATOR 2026–27 */}
      {/* ========================================================= */}
      <Dialog
        open={activeToolId === 'tax-calculator' || activeToolId === 'old-vs-new' || activeToolId === 'salary-tax'}
        onClose={() => setActiveToolId(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pb: 1 }}>
          <Box>
            <Typography variant="h5" fontWeight={800} color="#123D6B">
              Income Tax Calculator (Act 2025 vs Act 1961)
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Selected Framework: <strong>{taxResult.governingAct}</strong>
            </Typography>
          </Box>
          <IconButton onClick={() => setActiveToolId(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={700} color="#0F172A" gutterBottom>
                1. Income Breakdown (₹)
              </Typography>
              
              <TextField
                fullWidth
                size="small"
                label="Salary / Pension Income"
                type="number"
                value={taxInput.salaryIncome}
                onChange={(e) => setTaxInput({ ...taxInput, salaryIncome: Number(e.target.value) })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Business / Professional Income"
                type="number"
                value={taxInput.businessIncome}
                onChange={(e) => setTaxInput({ ...taxInput, businessIncome: Number(e.target.value) })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                size="small"
                label="Capital Gains (Stocks / Property)"
                type="number"
                value={taxInput.capitalGains}
                onChange={(e) => setTaxInput({ ...taxInput, capitalGains: Number(e.target.value) })}
                sx={{ mb: 2 }}
              />

              <Typography variant="subtitle2" fontWeight={700} color="#0F172A" sx={{ mt: 2, mb: 1 }}>
                2. Deductions (Chapter VI-A / Schedule XV)
              </Typography>

              <TextField
                fullWidth
                size="small"
                label="80C / Schedule XV (PPF, EPF, ELSS)"
                type="number"
                value={taxInput.deductions80C}
                onChange={(e) => setTaxInput({ ...taxInput, deductions80C: Number(e.target.value) })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                size="small"
                label="80D Medical Insurance"
                type="number"
                value={taxInput.deductions80D}
                onChange={(e) => setTaxInput({ ...taxInput, deductions80D: Number(e.target.value) })}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
                <Typography variant="caption" color="#FFD700" fontWeight="bold" display="block">
                  ESTIMATED TAX PAYABLE
                </Typography>
                <Typography variant="h3" fontWeight={800} color="#FFD700" sx={{ my: 1 }}>
                  ₹{taxResult.totalTaxPayable.toLocaleString('en-IN')}
                </Typography>

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="#94A3B8">Gross Income:</Typography>
                  <Typography variant="body2" fontWeight="bold">₹{taxResult.grossIncome.toLocaleString('en-IN')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="#94A3B8">Deductions Allowed:</Typography>
                  <Typography variant="body2" fontWeight="bold" color="#10B981">- ₹{taxResult.totalDeductions.toLocaleString('en-IN')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="#94A3B8">Taxable Income:</Typography>
                  <Typography variant="body2" fontWeight="bold">₹{taxResult.taxableIncome.toLocaleString('en-IN')}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="#94A3B8">Rebate (Sec 87A / 112):</Typography>
                  <Typography variant="body2" fontWeight="bold" color="#10B981">₹{taxResult.rebate87A.toLocaleString('en-IN')}</Typography>
                </Box>

                <Alert severity="info" sx={{ mt: 2, bgcolor: 'rgba(59, 130, 246, 0.1)', color: '#93C5FD', border: '1px solid rgba(59, 130, 246, 0.2)' }}>
                  Framework: <strong>{taxResult.governingAct}</strong>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'space-between' }}>
          <Button onClick={() => setActiveToolId(null)}>Close</Button>
          <Button variant="contained" disableElevation href="/service/income-tax-filing" sx={{ bgcolor: '#C59B27', color: '#0F172A', fontWeight: 800 }}>
            File ITR with Fingard CA Team →
          </Button>
        </DialogActions>
      </Dialog>

      {/* ========================================================= */}
      {/* TOOL MODAL 2: OLD SECTION ➔ NEW SECTION CONVERTER */}
      {/* ========================================================= */}
      <Dialog
        open={activeToolId === 'section-converter' || activeToolId === 'tax-navigator'}
        onClose={() => setActiveToolId(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">
            Old Section ➔ New Section Converter (Act 2025)
          </Typography>
          <IconButton onClick={() => setActiveToolId(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <TextField
            fullWidth
            placeholder="Type section number (e.g. 80C, 24b, 80D, 115BAC, 44AD)..."
            value={sectionSearch}
            onChange={(e) => setSectionSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ mb: 3 }}
          />

          <Grid container spacing={2}>
            {filteredSections.map((item, idx) => (
              <Grid item xs={12} key={idx}>
                <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, border: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={`Old: ${item.oldSection}`} color="default" size="small" sx={{ fontWeight: 'bold' }} />
                      <Typography fontWeight="bold" color="#123D6B">➔</Typography>
                      <Chip label={`New 2025 Act: ${item.newSection}`} color="primary" size="small" sx={{ fontWeight: 'bold', bgcolor: '#123D6B' }} />
                    </Box>
                    <Chip label={item.effectiveYear} size="small" sx={{ bgcolor: '#FEF3C7', color: '#92400E', fontWeight: 'bold' }} />
                  </Box>
                  <Typography variant="subtitle1" fontWeight={700} color="#0F172A">
                    {item.topic}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    <strong>1961 Act:</strong> {item.oldDetails}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    <strong>2025 Act:</strong> {item.newDetails}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* TOOL MODAL 3: AY / TAX YEAR CONVERTER */}
      {/* ========================================================= */}
      <Dialog
        open={activeToolId === 'ay-converter'}
        onClose={() => setActiveToolId(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 1 } }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">
            Tax Year / AY Converter
          </Typography>
          <IconButton onClick={() => setActiveToolId(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <TextField
            fullWidth
            label="Enter Assessment Year or Tax Year"
            value={ayInput}
            onChange={(e) => setAyInput(e.target.value)}
            helperText="Example: 2026-27, 2025-26"
            sx={{ mb: 3 }}
          />

          <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Typography variant="caption" color="#FFD700" fontWeight="bold">CONVERSION RESULT</Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={6}>
                <Typography variant="caption" color="#94A3B8">Assessment Year:</Typography>
                <Typography variant="h6" fontWeight="bold">{ayResult.assessmentYear}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="#94A3B8">Financial Year:</Typography>
                <Typography variant="h6" fontWeight="bold">{ayResult.financialYear}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="caption" color="#94A3B8">Governing Framework:</Typography>
                <Typography variant="body1" fontWeight="bold" color="#FFD700">{ayResult.governingAct}</Typography>
              </Grid>
            </Grid>
            <Alert severity="info" sx={{ mt: 2, bgcolor: 'rgba(255,255,255,0.05)', color: '#CBD5E1', border: '1px solid rgba(255,255,255,0.1)' }}>
              {ayResult.notes}
            </Alert>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* NEW TOOL MODALS: SIP, PPF, GST, Gratuity */}
      {/* ========================================================= */}

      {/* SIP Calculator */}
      <Dialog open={activeToolId === 'sip-calculator'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">SIP Calculator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Monthly Investment (₹)" type="number" value={sipState.monthlyInvestment} onChange={(e) => setSipState({...sipState, monthlyInvestment: Number(e.target.value)})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Expected Return Rate (%)" type="number" value={sipState.rate} onChange={(e) => setSipState({...sipState, rate: Number(e.target.value)})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Time Period (Years)" type="number" value={sipState.years} onChange={(e) => setSipState({...sipState, years: Number(e.target.value)})} /></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Total Invested:</Typography><Typography fontWeight="bold">₹{Math.round(sipResult.invested).toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Est. Returns:</Typography><Typography fontWeight="bold" color="#10B981">+ ₹{Math.round(sipResult.returns).toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Total Value:</Typography><Typography variant="h5" fontWeight="bold" color="#FFD700">₹{Math.round(sipResult.maturity).toLocaleString('en-IN')}</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* PPF Calculator */}
      <Dialog open={activeToolId === 'ppf-calculator'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">PPF Calculator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Yearly Investment (₹)" type="number" value={ppfState.yearlyInvestment} onChange={(e) => setPpfState({...ppfState, yearlyInvestment: Number(e.target.value)})} helperText="Max allowed under 80C is ₹1,50,000" /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Time Period (Years)" type="number" value={ppfState.years} onChange={(e) => setPpfState({...ppfState, years: Number(e.target.value)})} helperText="PPF has a minimum lock-in of 15 years" /></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Total Invested:</Typography><Typography fontWeight="bold">₹{Math.round(ppfResult.invested).toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Total Interest (7.1%):</Typography><Typography fontWeight="bold" color="#10B981">+ ₹{Math.round(ppfResult.returns).toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Maturity Value:</Typography><Typography variant="h5" fontWeight="bold" color="#FFD700">₹{Math.round(ppfResult.maturity).toLocaleString('en-IN')}</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* GST Calculator */}
      <Dialog open={activeToolId === 'gst-calculator'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">GST Calculator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Amount (₹)" type="number" value={gstState.amount} onChange={(e) => setGstState({...gstState, amount: Number(e.target.value)})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="GST Rate (%)" type="number" value={gstState.rate} onChange={(e) => setGstState({...gstState, rate: Number(e.target.value)})} /></Grid>
            <Grid item xs={6}>
              <TextField select fullWidth label="Calculation Type" value={gstState.type} onChange={(e) => setGstState({...gstState, type: e.target.value as any})} SelectProps={{ native: true }}>
                <option value="exclusive">Add GST (Exclusive)</option>
                <option value="inclusive">Remove GST (Inclusive)</option>
              </TextField>
            </Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Original Base Amount:</Typography><Typography fontWeight="bold">₹{Math.round(gstResult.original).toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>GST Amount ({gstState.rate}%):</Typography><Typography fontWeight="bold" color="#F59E0B">₹{Math.round(gstResult.gstAmount).toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Net Price:</Typography><Typography variant="h5" fontWeight="bold" color="#FFD700">₹{Math.round(gstResult.net).toLocaleString('en-IN')}</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* Gratuity Calculator */}
      <Dialog open={activeToolId === 'gratuity-calculator'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">Gratuity Calculator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Last Drawn Basic Salary + DA (₹)" type="number" value={gratuityState.salary} onChange={(e) => setGratuityState({...gratuityState, salary: Number(e.target.value)})} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Years of Service" type="number" value={gratuityState.years} onChange={(e) => setGratuityState({...gratuityState, years: Number(e.target.value)})} helperText="Must be greater than 5 years to be eligible" /></Grid>
          </Grid>
          {gratuityState.years >= 5 ? (
            <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Estimated Gratuity Payable:</Typography><Typography variant="h5" fontWeight="bold" color="#10B981">₹{Math.round(gratuityResult.amount).toLocaleString('en-IN')}</Typography></Box>
            </Paper>
          ) : (
            <Alert severity="warning" sx={{ mt: 3 }}>You must complete at least 5 years of continuous service with the same employer to be eligible for gratuity.</Alert>
          )}
        </DialogContent>
      </Dialog>


      {/* NPS Calculator */}
      <Dialog open={activeToolId === 'nps-calculator'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">NPS Calculator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Current Age" type="number" value={npsState.age} onChange={(e) => setNpsState({...npsState, age: Number(e.target.value)})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Monthly Investment (₹)" type="number" value={npsState.monthly} onChange={(e) => setNpsState({...npsState, monthly: Number(e.target.value)})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Expected Return (%)" type="number" value={npsState.rate} onChange={(e) => setNpsState({...npsState, rate: Number(e.target.value)})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Annuity Return (%)" type="number" value={npsState.annuityRate} onChange={(e) => setNpsState({...npsState, annuityRate: Number(e.target.value)})} helperText="Post-retirement rate" /></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Total Corpus at 60:</Typography><Typography fontWeight="bold">₹{Math.round(npsResult.corpus).toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Tax-Free Lump Sum (60%):</Typography><Typography fontWeight="bold" color="#10B981">₹{Math.round(npsResult.lumpSum).toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Est. Monthly Pension:</Typography><Typography variant="h5" fontWeight="bold" color="#FFD700">₹{Math.round(npsResult.monthlyPension).toLocaleString('en-IN')}</Typography></Box>
            <Alert severity="success" sx={{ mt: 2, py: 0 }}>Extra Tax Saved (Sec 80CCD 1B): ₹{Math.round(npsResult.taxSaved).toLocaleString('en-IN')} / year</Alert>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* Startup Valuation */}
      <Dialog open={activeToolId === 'startup-valuation'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">Startup Valuation (Revenue Multiple)</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Annual Revenue (ARR in ₹)" type="number" value={valState.revenue} onChange={(e) => setValState({...valState, revenue: Number(e.target.value)})} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Industry Revenue Multiple (x)" type="number" value={valState.multiple} onChange={(e) => setValState({...valState, multiple: Number(e.target.value)})} helperText="SaaS is typically 5x-10x, E-com 1x-3x" /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Cash in Bank (₹)" type="number" value={valState.cash} onChange={(e) => setValState({...valState, cash: Number(e.target.value)})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Total Debt (₹)" type="number" value={valState.debt} onChange={(e) => setValState({...valState, debt: Number(e.target.value)})} /></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Enterprise Value (EV):</Typography><Typography fontWeight="bold">₹{Math.round(valResult.ev).toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Net Cash (+Cash -Debt):</Typography><Typography fontWeight="bold" color="#10B981">₹{Math.round(valState.cash - valState.debt).toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Pre-Money Equity Value:</Typography><Typography variant="h5" fontWeight="bold" color="#FFD700">₹{Math.round(valResult.equityVal).toLocaleString('en-IN')}</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* HSN Code Finder */}
      <Dialog open={activeToolId === 'hsn-finder'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">HSN / SAC Code Finder</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3, textAlign: 'center' }}>
          <TextField fullWidth placeholder="E.g., Software, Consulting, Laptops..." variant="outlined" InputProps={{ startAdornment: <InputAdornment position="start"><Search /></InputAdornment> }} />
          <Box sx={{ mt: 4, p: 3, bgcolor: '#F8FAFC', borderRadius: 2 }}>
            <Typography color="text.secondary">Enter a product or service name above to instantly find its HSN/SAC code and applicable GST slab rate.</Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Invoice Generator */}
      <Dialog open={activeToolId === 'invoice-generator'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">GST Invoice Generator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField fullWidth label="Client / Company Name" variant="outlined" /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Client GSTIN" variant="outlined" /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Invoice Date" type="date" InputLabelProps={{ shrink: true }} variant="outlined" /></Grid>
            <Grid item xs={8}><TextField fullWidth label="Item Description" variant="outlined" /></Grid>
            <Grid item xs={4}><TextField fullWidth label="Amount (₹)" type="number" variant="outlined" /></Grid>
          </Grid>
          <Button fullWidth variant="contained" size="large" sx={{ mt: 3, bgcolor: '#123D6B', py: 1.5, fontWeight: 'bold' }} onClick={() => alert('PDF generation module will initialize shortly!')}>
            Generate & Download PDF
          </Button>
        </DialogContent>
      </Dialog>


      {/* ========================================================= */}
      {/* CAPITAL GAINS TAX CALCULATOR */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === 'capital-gains'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">Capital Gains Tax Calculator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField select fullWidth label="Asset Type" value={cgState.asset} onChange={(e) => setCgState({...cgState, asset: e.target.value as any})}><MenuItem value="equity">Equity / Mutual Funds</MenuItem><MenuItem value="property">Real Estate / Gold</MenuItem><MenuItem value="debt">Debt Funds / Bonds</MenuItem></TextField></Grid>
            <Grid item xs={6}><TextField fullWidth label="Purchase Price (₹)" type="number" value={cgState.buyPrice} onChange={(e) => setCgState({...cgState, buyPrice: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Sale Price (₹)" type="number" value={cgState.sellPrice} onChange={(e) => setCgState({...cgState, sellPrice: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={12}><TextField fullWidth label="Holding Period (Months)" type="number" value={cgState.months} onChange={(e) => setCgState({...cgState, months: Math.max(0, Number(e.target.value))})} helperText={cgState.asset === 'equity' ? 'LTCG threshold: >12 months' : 'LTCG threshold: >24 months'} /></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Alert severity={cgResult.type === 'No Gain' ? 'warning' : 'info'} sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.05)', color: '#CBD5E1', border: '1px solid rgba(255,255,255,0.1)' }}>{cgResult.type} — Taxed at {cgResult.rate}%</Alert>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Capital Gain:</Typography><Typography fontWeight="bold">₹{cgResult.gain.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Taxable Gain:</Typography><Typography fontWeight="bold">₹{cgResult.taxable.toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Estimated Tax:</Typography><Typography variant="h5" fontWeight="bold" color="#EF4444">₹{cgResult.tax.toLocaleString('en-IN')}</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* BUSINESS & FREELANCER TAX (44AD / 44ADA) */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === 'business-tax'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">Business & Freelancer Tax</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField select fullWidth label="Scheme" value={bizState.scheme} onChange={(e) => setBizState({...bizState, scheme: e.target.value as any})}><MenuItem value="44AD">Sec 44AD (Business)</MenuItem><MenuItem value="44ADA">Sec 44ADA (Professionals)</MenuItem></TextField></Grid>
            <Grid item xs={6}><TextField select fullWidth label="Regime" value={bizState.regime} onChange={(e) => setBizState({...bizState, regime: e.target.value as any})}><MenuItem value="new">New Regime</MenuItem><MenuItem value="old">Old Regime</MenuItem></TextField></Grid>
            <Grid item xs={12}><TextField fullWidth label="Annual Gross Turnover (₹)" type="number" value={bizState.turnover} onChange={(e) => setBizState({...bizState, turnover: Math.max(0, Number(e.target.value))})} helperText={bizState.scheme === '44AD' ? 'Limit: ₹3 Cr (digital) / ₹2 Cr (cash)' : 'Limit: ₹75 Lakhs'} /></Grid>
            {bizState.scheme === '44AD' && <Grid item xs={12}><TextField fullWidth label="Digital Receipts (%)" type="number" value={bizState.digitalPercent} onChange={(e) => setBizState({...bizState, digitalPercent: Math.min(100, Math.max(0, Number(e.target.value)))})} helperText="Digital = 6% presumptive, Cash = 8% presumptive" /></Grid>}
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Presumptive Income:</Typography><Typography fontWeight="bold">₹{Math.round(bizResult.presumptiveIncome).toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Tax (incl. 4% Cess):</Typography><Typography fontWeight="bold" color="#EF4444">₹{bizResult.tax.toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Effective Tax Rate:</Typography><Typography variant="h5" fontWeight="bold" color="#FFD700">{bizResult.effectiveRate}%</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* CRYPTO / VDA TAX CALCULATOR */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === 'crypto-tax'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">Crypto / VDA Tax Calculator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>Flat 30% tax. No deductions allowed except cost of acquisition. No set-off of losses.</Alert>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Purchase Price (₹)" type="number" value={cryptoState.buyPrice} onChange={(e) => setCryptoState({...cryptoState, buyPrice: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Sale Price (₹)" type="number" value={cryptoState.sellPrice} onChange={(e) => setCryptoState({...cryptoState, sellPrice: Math.max(0, Number(e.target.value))})} /></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Capital Gain:</Typography><Typography fontWeight="bold">₹{cryptoResult.gain.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Tax (30%):</Typography><Typography fontWeight="bold" color="#EF4444">₹{cryptoResult.tax.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Cess (4%):</Typography><Typography fontWeight="bold">₹{cryptoResult.cess.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>TDS Deducted (1% of Sale):</Typography><Typography fontWeight="bold" color="#F59E0B">₹{cryptoResult.tds.toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Net Profit After Tax:</Typography><Typography variant="h5" fontWeight="bold" color="#10B981">₹{cryptoResult.netProfit.toLocaleString('en-IN')}</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* TDS CALCULATOR */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === 'tds-calculator'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">TDS Calculator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}><TextField select fullWidth label="TDS Section" value={tdsState.section} onChange={(e) => setTdsState({...tdsState, section: e.target.value})}><MenuItem value="194J">194J – Professional / Technical Fees (10%)</MenuItem><MenuItem value="194A">194A – Interest Non-Bank (10%)</MenuItem><MenuItem value="194I_land">194I – Rent on Land/Building (10%)</MenuItem><MenuItem value="194I_equip">194I – Rent on Equipment (2%)</MenuItem><MenuItem value="194C_ind">194C – Contractor Individual (1%)</MenuItem><MenuItem value="194C_co">194C – Contractor Company (2%)</MenuItem><MenuItem value="194H">194H – Brokerage / Commission (5%)</MenuItem><MenuItem value="194B">194B – Lottery / Game Winnings (30%)</MenuItem><MenuItem value="194N">194N – Cash Withdrawal {'>'} ₹1Cr (2%)</MenuItem></TextField></Grid>
            <Grid item xs={12}><TextField fullWidth label="Gross Payment Amount (₹)" type="number" value={tdsState.gross} onChange={(e) => setTdsState({...tdsState, gross: Math.max(0, Number(e.target.value))})} /></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.05)', color: '#CBD5E1', border: '1px solid rgba(255,255,255,0.1)' }}>{tdsResult.label}</Alert>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>TDS Rate:</Typography><Typography fontWeight="bold">{tdsResult.rate}%</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>TDS Deducted:</Typography><Typography fontWeight="bold" color="#EF4444">₹{tdsResult.deducted.toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Net Payable to Payee:</Typography><Typography variant="h5" fontWeight="bold" color="#10B981">₹{tdsResult.netPayable.toLocaleString('en-IN')}</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* ADVANCE TAX CALCULATOR */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === 'advance-tax'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">Advance Tax Calculator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Total Estimated Tax (₹)" type="number" value={advTaxState.totalTax} onChange={(e) => setAdvTaxState({...advTaxState, totalTax: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="TDS Already Paid (₹)" type="number" value={advTaxState.tdsPaid} onChange={(e) => setAdvTaxState({...advTaxState, tdsPaid: Math.max(0, Number(e.target.value))})} /></Grid>
          </Grid>
          {advTaxResult.net < 10000 ? (
            <Alert severity="success" sx={{ mt: 3 }}>Your net tax liability is below ₹10,000. Advance tax is NOT applicable to you.</Alert>
          ) : (
            <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
              <Typography variant="subtitle2" color="#94A3B8" gutterBottom>Quarterly Installment Schedule</Typography>
              {[{ q: 'Q1 – By Jun 15', amt: advTaxResult.q1, cum: '15%' }, { q: 'Q2 – By Sep 15', amt: advTaxResult.q2, cum: '45%' }, { q: 'Q3 – By Dec 15', amt: advTaxResult.q3, cum: '75%' }, { q: 'Q4 – By Mar 15', amt: advTaxResult.q4, cum: '100%' }].map((item) => (
                <Box key={item.q} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, py: 1, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                  <Typography>{item.q} <Chip label={item.cum} size="small" sx={{ ml: 1, bgcolor: 'rgba(255,255,255,0.1)', color: '#FFD700', fontSize: '11px' }} /></Typography>
                  <Typography fontWeight="bold">₹{item.amt.toLocaleString('en-IN')}</Typography>
                </Box>
              ))}
              <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Total Advance Tax:</Typography><Typography variant="h5" fontWeight="bold" color="#FFD700">₹{advTaxResult.net.toLocaleString('en-IN')}</Typography></Box>
            </Paper>
          )}
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* LATE FILING & INTEREST (234A, 234B, 234F) */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === 'late-interest'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">Late Filing & Interest Calculator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Total Tax Liability (₹)" type="number" value={lateState.taxPayable} onChange={(e) => setLateState({...lateState, taxPayable: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Tax Already Paid (₹)" type="number" value={lateState.taxPaid} onChange={(e) => setLateState({...lateState, taxPaid: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Months of Delay" type="number" value={lateState.monthsLate} onChange={(e) => setLateState({...lateState, monthsLate: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Total Income (₹)" type="number" value={lateState.income} onChange={(e) => setLateState({...lateState, income: Math.max(0, Number(e.target.value))})} helperText="For 234F fee calculation" /></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Unpaid Tax:</Typography><Typography fontWeight="bold">₹{lateResult.unpaid.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Interest 234A (1%/mo):</Typography><Typography fontWeight="bold" color="#EF4444">₹{lateResult.interest234A.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Interest 234B (1%/mo):</Typography><Typography fontWeight="bold" color="#EF4444">₹{lateResult.interest234B.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Late Fee 234F:</Typography><Typography fontWeight="bold" color="#F59E0B">₹{lateResult.fee234F.toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Total Penalty:</Typography><Typography variant="h5" fontWeight="bold" color="#EF4444">₹{lateResult.total.toLocaleString('en-IN')}</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* 80C TAX SAVING OPTIMIZER */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === '80c-calculator'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">80C Tax Saving Optimizer</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="EPF Contribution (₹)" type="number" value={sec80cState.epf} onChange={(e) => setSec80cState({...sec80cState, epf: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="PPF (₹)" type="number" value={sec80cState.ppf} onChange={(e) => setSec80cState({...sec80cState, ppf: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="ELSS / Tax Saver MF (₹)" type="number" value={sec80cState.elss} onChange={(e) => setSec80cState({...sec80cState, elss: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="LIC Premium (₹)" type="number" value={sec80cState.lic} onChange={(e) => setSec80cState({...sec80cState, lic: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Children Tuition (₹)" type="number" value={sec80cState.tuition} onChange={(e) => setSec80cState({...sec80cState, tuition: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField select fullWidth label="Your Tax Slab" value={sec80cState.slab} onChange={(e) => setSec80cState({...sec80cState, slab: Number(e.target.value)})}><MenuItem value={5}>5%</MenuItem><MenuItem value={10}>10%</MenuItem><MenuItem value={15}>15%</MenuItem><MenuItem value={20}>20%</MenuItem><MenuItem value={30}>30%</MenuItem></TextField></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Total Investments:</Typography><Typography fontWeight="bold">₹{sec80cResult.total.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Eligible (Max ₹1.5L):</Typography><Typography fontWeight="bold">₹{sec80cResult.eligible.toLocaleString('en-IN')}</Typography></Box>
            {sec80cResult.remaining > 0 && <Alert severity="warning" sx={{ my: 1, bgcolor: 'rgba(255,255,255,0.05)', color: '#FFD700', border: '1px solid rgba(255,215,0,0.2)' }}>You can still invest ₹{sec80cResult.remaining.toLocaleString('en-IN')} more to max out 80C!</Alert>}
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Tax Saved (incl. Cess):</Typography><Typography variant="h5" fontWeight="bold" color="#10B981">₹{sec80cResult.taxSaved.toLocaleString('en-IN')}</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* HRA CALCULATOR */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === 'hra-calculator'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">HRA Exemption Calculator</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Basic Salary / mo (₹)" type="number" value={hraState.basic} onChange={(e) => setHraState({...hraState, basic: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="DA / mo (₹)" type="number" value={hraState.da} onChange={(e) => setHraState({...hraState, da: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="HRA Received / mo (₹)" type="number" value={hraState.hraReceived} onChange={(e) => setHraState({...hraState, hraReceived: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Rent Paid / mo (₹)" type="number" value={hraState.rentPaid} onChange={(e) => setHraState({...hraState, rentPaid: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Checkbox checked={hraState.metro} onChange={(e) => setHraState({...hraState, metro: e.target.checked})} />} label="Metro City (Delhi, Mumbai, Kolkata, Chennai)" /></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Typography variant="subtitle2" color="#94A3B8" gutterBottom>Minimum of these three (Annual):</Typography>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Actual HRA Received:</Typography><Typography fontWeight="bold">₹{hraResult.a1.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Rent – 10% Salary:</Typography><Typography fontWeight="bold">₹{hraResult.a2.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>{hraState.metro ? '50%' : '40%'} of Salary:</Typography><Typography fontWeight="bold">₹{hraResult.a3.toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Annual Exempt HRA:</Typography><Typography variant="h5" fontWeight="bold" color="#10B981">₹{hraResult.exempt.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Annual Taxable HRA:</Typography><Typography fontWeight="bold" color="#EF4444">₹{hraResult.taxable.toLocaleString('en-IN')}</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* HOME LOAN TAX BENEFIT */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === 'homeloan-calculator'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">Home Loan Tax Benefit</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Loan Amount (₹)" type="number" value={hlState.principal} onChange={(e) => setHlState({...hlState, principal: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Interest Rate (%)" type="number" value={hlState.interestRate} onChange={(e) => setHlState({...hlState, interestRate: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Yearly Principal Repaid (₹)" type="number" value={hlState.yearlyPrincipal} onChange={(e) => setHlState({...hlState, yearlyPrincipal: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Yearly Interest Paid (₹)" type="number" value={hlState.yearlyInterest} onChange={(e) => setHlState({...hlState, yearlyInterest: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={12}><FormControlLabel control={<Checkbox checked={hlState.selfOccupied} onChange={(e) => setHlState({...hlState, selfOccupied: e.target.checked})} />} label="Self-Occupied Property (Interest cap: ₹2L under Sec 24b)" /></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Principal Benefit (80C):</Typography><Typography fontWeight="bold">₹{hlResult.principalBenefit.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Interest Benefit (Sec 24b):</Typography><Typography fontWeight="bold">₹{hlResult.interestBenefit.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Est. EMI:</Typography><Typography fontWeight="bold" color="#F59E0B">₹{hlResult.emi.toLocaleString('en-IN')}/mo</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Total Deduction:</Typography><Typography variant="h5" fontWeight="bold" color="#FFD700">₹{hlResult.totalBenefit.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}><Typography>Tax Saved (30% slab):</Typography><Typography fontWeight="bold" color="#10B981">₹{hlResult.taxSaved30.toLocaleString('en-IN')}</Typography></Box>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* ITR ELIGIBILITY CHECKER */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === 'itr-eligibility'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">ITR Form Eligibility Checker</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Select all that apply to your income profile:</Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            <FormControlLabel control={<Checkbox checked={itrState.hasSalary} onChange={(e) => setItrState({...itrState, hasSalary: e.target.checked})} />} label="Salary / Pension Income" />
            <FormControlLabel control={<Checkbox checked={itrState.hasBusinessIncome} onChange={(e) => setItrState({...itrState, hasBusinessIncome: e.target.checked})} />} label="Business or Freelance Income" />
            <FormControlLabel control={<Checkbox checked={itrState.hasCG} onChange={(e) => setItrState({...itrState, hasCG: e.target.checked})} />} label="Capital Gains (Stocks, Property, MF)" />
            <FormControlLabel control={<Checkbox checked={itrState.hasMultipleHP} onChange={(e) => setItrState({...itrState, hasMultipleHP: e.target.checked})} />} label="Multiple House Properties" />
            <FormControlLabel control={<Checkbox checked={itrState.foreignAssets} onChange={(e) => setItrState({...itrState, foreignAssets: e.target.checked})} />} label="Foreign Assets or Income" />
            <FormControlLabel control={<Checkbox checked={itrState.isDirector} onChange={(e) => setItrState({...itrState, isDirector: e.target.checked})} />} label="Director in a Company" />
            <FormControlLabel control={<Checkbox checked={itrState.hasUnlisted} onChange={(e) => setItrState({...itrState, hasUnlisted: e.target.checked})} />} label="Unlisted Equity Shares" />
            {itrState.hasBusinessIncome && <FormControlLabel control={<Checkbox checked={itrState.turnoverAbove1Cr} onChange={(e) => setItrState({...itrState, turnoverAbove1Cr: e.target.checked})} />} label="Business Turnover above ₹1 Crore" />}
          </Box>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF', textAlign: 'center' }}>
            <Typography variant="h4" fontWeight="bold" color="#FFD700">{itrForm.form}</Typography>
            <Typography variant="body2" color="#CBD5E1" sx={{ mt: 1 }}>{itrForm.reason}</Typography>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* FORM CONVERTER */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === 'form-converter'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">Form Converter (1961 → 2025 Act)</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          {[
            { old: 'ITR-1 (Sahaj)', new: 'ITR-1 (Sahaj)', note: 'Salary/Pension, One HP, Other Sources' },
            { old: 'ITR-2', new: 'ITR-2', note: 'Capital Gains, Foreign Assets, Multiple HP' },
            { old: 'ITR-3', new: 'ITR-3', note: 'Business/Profession (Non-presumptive)' },
            { old: 'ITR-4 (Sugam)', new: 'ITR-4 (Sugam)', note: 'Presumptive Income (44AD/44ADA/44AE)' },
            { old: 'ITR-5', new: 'ITR-5', note: 'LLP, AOP, BOI, Firms' },
            { old: 'ITR-6', new: 'ITR-6', note: 'Companies (Non Sec 11)' },
            { old: 'ITR-7', new: 'ITR-7', note: 'Trusts, Political Parties' },
            { old: 'ITR-U (Updated)', new: 'ITR-U', note: 'Updated Return within 24 months' },
            { old: 'Form 16', new: 'Form 16', note: 'TDS Certificate – Salary' },
            { old: 'Form 26AS', new: 'AIS / TIS', note: 'Annual Information Statement' },
          ].map((item, idx) => (
            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid #E2E8F0' }}>
              <Box><Typography fontWeight="bold" color="#123D6B">{item.old}</Typography><Typography variant="caption" color="text.secondary">{item.note}</Typography></Box>
              <Box sx={{ textAlign: 'right' }}><ArrowForward sx={{ color: '#C59B27', mx: 1 }} /><Typography fontWeight="bold" color="#10B981" component="span">{item.new}</Typography></Box>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* NRI TAX CALCULATOR & DTAA */}
      {/* ========================================================= */}
      <Dialog open={activeToolId === 'nri-calculator'} onClose={() => setActiveToolId(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 4, p: 1 } }}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h5" fontWeight={800} color="#123D6B">NRI Tax Calculator & DTAA</Typography>
          <IconButton onClick={() => setActiveToolId(null)}><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ py: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={6}><TextField fullWidth label="Days in India (FY)" type="number" value={nriState.daysInIndia} onChange={(e) => setNriState({...nriState, daysInIndia: Math.max(0, Math.min(366, Number(e.target.value)))})} helperText="NR: <120 days, RNOR: 120-181, ROR: 182+" /></Grid>
            <Grid item xs={6}><TextField select fullWidth label="DTAA Country" value={nriState.dtaaCountry} onChange={(e) => setNriState({...nriState, dtaaCountry: e.target.value})}><MenuItem value="USA">USA</MenuItem><MenuItem value="UK">UK</MenuItem><MenuItem value="UAE">UAE</MenuItem><MenuItem value="Canada">Canada</MenuItem><MenuItem value="Singapore">Singapore</MenuItem><MenuItem value="Australia">Australia</MenuItem><MenuItem value="Germany">Germany</MenuItem></TextField></Grid>
            <Grid item xs={6}><TextField fullWidth label="Indian Income (₹)" type="number" value={nriState.indianIncome} onChange={(e) => setNriState({...nriState, indianIncome: Math.max(0, Number(e.target.value))})} /></Grid>
            <Grid item xs={6}><TextField fullWidth label="Foreign Income (₹)" type="number" value={nriState.foreignIncome} onChange={(e) => setNriState({...nriState, foreignIncome: Math.max(0, Number(e.target.value))})} /></Grid>
          </Grid>
          <Paper elevation={0} sx={{ mt: 3, p: 3, borderRadius: 3, bgcolor: '#0F172A', color: '#FFFFFF' }}>
            <Alert severity="info" sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.05)', color: '#CBD5E1', border: '1px solid rgba(255,255,255,0.1)' }}>Residential Status: <strong>{nriResult.status}</strong></Alert>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Taxable Income in India:</Typography><Typography fontWeight="bold">₹{nriResult.taxableIncome.toLocaleString('en-IN')}</Typography></Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}><Typography>Tax (New Regime + Cess):</Typography><Typography fontWeight="bold" color="#EF4444">₹{nriResult.tax.toLocaleString('en-IN')}</Typography></Box>
            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 2 }} />
            <Alert severity="success" sx={{ bgcolor: 'rgba(255,255,255,0.05)', color: '#10B981', border: '1px solid rgba(16,185,129,0.2)' }}>DTAA ({nriState.dtaaCountry}): {nriResult.dtaaRelief}</Alert>
          </Paper>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* UNIVERSAL FALLBACK (should no longer trigger) */}
      {/* ========================================================= */}
      <Dialog
        open={!!activeToolId && !['tax-calculator', 'old-vs-new', 'salary-tax', 'section-converter', 'tax-navigator', 'ay-converter', 'sip-calculator', 'ppf-calculator', 'gst-calculator', 'gratuity-calculator', 'nps-calculator', 'startup-valuation', 'hsn-finder', 'invoice-generator', 'capital-gains', 'business-tax', 'crypto-tax', 'tds-calculator', 'advance-tax', 'late-interest', '80c-calculator', 'hra-calculator', 'homeloan-calculator', 'itr-eligibility', 'form-converter', 'nri-calculator'].includes(activeToolId)}
        onClose={() => setActiveToolId(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4, p: 1, textAlign: 'center' } }}
      >
        <DialogTitle sx={{ pt: 4 }}>
          <Construction sx={{ fontSize: 50, color: '#C59B27', mb: 2 }} />
          <Typography variant="h5" fontWeight={800} color="#123D6B">
            Tool In Development
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pb: 3 }}>
          <Typography variant="body1" color="text.secondary">
            Our Chartered Accountants and engineers are currently fine-tuning this calculator to ensure 100% compliance and accuracy. It will be launched shortly!
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
          <Button variant="contained" onClick={() => setActiveToolId(null)} sx={{ bgcolor: '#123D6B', borderRadius: 8, px: 4 }}>
            Got it, thanks!
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TaxTools;
